import React = require('react');
import { Col, ControlLabel, FormGroup, OverlayTrigger, Popover, Row } from 'react-bootstrap';
import { AdmiralCard } from '../AdmiralCard';
import { Field, FieldArray, InjectedFormProps } from 'redux-form';
import { IDestination, IPremium, IProduct, IStateCities, TripType } from '../../../../../data/api/models';
import { IQuoteActions, IQuoteState } from '../../../../../services/quote/models';
import { difference, get, intersection, map } from 'lodash';
import { REGION_WORLDWIDE_EXCLUDING_LABEL_OLD } from '../../../index';
import FieldFormControl from '../../../../../components/FieldFormControl';
import { isRequired } from '../../../../../util/validators';
import { showDestinations, showRegions } from '../../../../../data/api/api';
import { RecalculateButton } from '../RecalculateButton';
import Destination from '../../../components/Destination';
import StateCities from '../../../components/StateCities';
import { FaQuestionCircle } from 'react-icons/fa';
import SelectControl from '../../../../../components/SelectControl';
import { II18nMessages } from '../../../../../services/branding/models';
import * as Commonmark from 'react-commonmark';
import { StartDateSelector } from '../../QuotePayment/Components/QuoteSummary/components/StartDateSelector';
import { EndDateSelector } from '../../QuotePayment/Components/QuoteSummary/components/EndDateSelector';
import { useEffect, useRef } from 'react';
import { IVeriskState } from '../../../../../services/verisk/models';
import { FormHelper } from '../../../../../helpers/form-helper';
import { RemoveUkRegion } from '../../../../../helpers/RemoveUkRegion';

interface IDestinationRegionSelectorProps extends InjectedFormProps {
  tripType: TripType;
  product: IProduct;
  region: string;
  quoteState: IQuoteState;
  startDate: string;
  i18n: II18nMessages;
  endDate: string;
  verisk: IVeriskState;
  hideDateSelector: boolean;
  createdBeforeFixedSchemeVersionRule: boolean;
  destinations: IDestination[];
  stateCities: IStateCities[];
  destinationsValue: string[];
  stateCitiesValue: string[];
  recalculate: () => any;
  actions: {
    quote: IQuoteActions;
  }
  premium?: IPremium;

  renderDefinition(name: string, useProvidedString?: boolean): any;
}

export const DestinationRegionSelector = ({
                                            product,
                                            createdBeforeFixedSchemeVersionRule,
                                            renderDefinition,
                                            tripType,
                                            recalculate,
                                            region,
                                            valid,
                                            startDate,
                                            i18n,
                                            endDate,
                                            actions,
                                            change,
                                            destinations,
                                            stateCities,
                                            quoteState,
                                            destinationsValue,
                                            stateCitiesValue,
                                            verisk,
                                            hideDateSelector = false,
                                            premium,
                                            ...props
                                          }: IDestinationRegionSelectorProps) => {

  const isAnnual = tripType === 'annual';

  const usePrevious = (value) => {
    const ref = useRef();
    useEffect(() => {
      ref.current = value;
    });
    return ref.current;
  }

  const previousValues = usePrevious({ destinationsValue, region });

  useEffect(() => {
    const previousDestinations = get(previousValues, 'destinationsValue');
    const previousRegion = get(previousValues, 'region');

    if (previousDestinations && difference(previousDestinations, destinationsValue).length > 0) {
      const hasEmpty = destinationsValue.includes('') || destinationsValue.includes(undefined)
      if (!hasEmpty) {
        actions.quote.recalculateScreening()
      }
    }

    if (previousRegion && (previousRegion !== region)) {
      actions.quote.recalculateScreening()
    }
  }, [region, destinationsValue])

  if (destinationsValue && destinationsValue.length && !intersection(destinationsValue, ['USA', 'United States']).length) {
    change('stateCities', ['']);
  }

  const hideUKRegion = get(premium, 'information.hideUkRegion', false)
  const productRegions = RemoveUkRegion(product.metadata.regions, props?.quoteForEdit, hideUKRegion)
  const hasUKRegion = get(productRegions, 'uk', null) !== null;

  const regionsList = map(productRegions, (name, key) => {
    if (key === 'worldwide_excluding' && createdBeforeFixedSchemeVersionRule) {
      return {
        label: REGION_WORLDWIDE_EXCLUDING_LABEL_OLD,
        value: key,
      };
    }

    return {
      label: name,
      value: key,
    };
  });

  const popoverClickRootClose = (
    <Popover id="popover-trigger-click-root-close" title="Regions">
      {hasUKRegion && <Commonmark source={product.metadata.definitions.region_uk}/>}
      <Commonmark source={product.metadata.definitions.region_europe}/>
      <Commonmark source={
        createdBeforeFixedSchemeVersionRule
          ? product.metadata.definitions.region_worldwide_excluding_old
          : product.metadata.definitions.region_worldwide_excluding
      }
      />
      <Commonmark source={
        createdBeforeFixedSchemeVersionRule
          ? product.metadata.definitions.region_worldwide_old
          : product.metadata.definitions.region_worldwide
      }
      />
    </Popover>
  );

  const renderStartDate = () => {
    return (
      <>
        {!hideDateSelector && (
          <StartDateSelector
            tripType={tripType}
            renderDefinition={renderDefinition}
            change={change}
            startDate={startDate}/>
        )}
      </>
    )
  }

  const renderEndDate = () => {
    return (
      <>
        {!hideDateSelector && (<EndDateSelector startDate={startDate} renderDefinition={renderDefinition}/>)}
      </>
    )
  }

  const renderRegions = () => {
    const europeText = 'Cover for 50 European countries, and our policies stretch the boundaries of Europe to include places like: Egypt, Morocco, Tunisia, Turkey.'
    return (
      <>
        <Row>
          {map(regionsList, (list, key) => {
            const isSelected = region === list.value;
            return (
              <Col xs={12} className="cover-type-btn-destination-container">
                <div className={isSelected ? 'box selected-trip-type' : 'box'}>
                  <Field
                    name="region"
                    id={key + '-btn'}
                    type="radio"
                    value={list.value}
                    component={FieldFormControl}
                    validate={[isRequired]}
                  >
                    <div className={`trip-type-btn ${key}`}>
                      <div className={'trip-type-selection-container'}>
                        <div className={'title'}>{list.label}</div>
                        <div className={'sub-text'}>{isSelected && region === 'europe' ? europeText : ''}</div>
                      </div>
                    </div>
                  </Field>
                </div>
              </Col>
            )
          })}
        </Row>
        {renderStartDate()}
      </>
    );
  }

  const renderDestinations = () => {
    return (
      <div>
        {renderStartDate()}
        {!isAnnual && (
          <>
            {renderEndDate()}
          </>
        )}
        {showDestinations(tripType) ? (
          <div style={{ marginBottom: '20px' }}>
            <FieldArray
              name="destinations"
              i18n={i18n}
              destinations={destinations}
              component={Destination}
              destinationsValue={destinationsValue}
              renderDefinition={renderDefinition}
            />
            <FieldArray
              name="stateCities"
              i18n={i18n}
              stateCities={stateCities}
              component={StateCities}
              stateCitiesValue={stateCitiesValue}
              destinationsValue={destinationsValue}
              renderDefinition={renderDefinition}
            />
          </div>
        ) : (
          <>
            {FormHelper.disabledValidationField('destinations[0]')}
            {FormHelper.disabledValidationField('stateCities[0]')}
          </>
        )}

        {showRegions(tripType) && (
          <div>
            {i18n.quote.tripDetails.destinations} {renderDefinition('region')}
            <Row className="margin-top">
              <Col sm={12}>
                <FormGroup controlId="destination">
                  <Col componentClass={ControlLabel} sm={3}>
                    {i18n.quote.tripDetails.region}{' '}
                    <OverlayTrigger
                      trigger="click"
                      rootClose={true}
                      placement="right"
                      overlay={popoverClickRootClose}
                    ><FaQuestionCircle className="info-btn"/>
                    </OverlayTrigger>
                  </Col>
                  <Col sm={9} className="destinations-inputs">
                    <Field
                      name="region"
                      id="destinations-inputs"
                      type="text"
                      component={SelectControl}
                      forceFilter={false}
                      options={regionsList}
                      validate={[
                        isRequired,
                      ]}
                    /></Col>
                </FormGroup>
                <div className="info-note">
                  {i18n.quote.tripDetails.europeanIncluded}{' '}
                </div>
              </Col>
            </Row>
          </div>
        )}
      </div>
    )
  }

  return (
    <AdmiralCard icon={'globe.svg'}
                 title={isAnnual ? 'Which region do you plan to travel in?' : 'Which destinations do you plan to travel'}>
      <div>
        {isAnnual && renderRegions()}
        {!isAnnual && renderDestinations()}
        <RecalculateButton handleRecalculate={recalculate} valid={valid} block={true}
                           isCalculating={quoteState.isCalculating || verisk.isLoading}/>
      </div>
    </AdmiralCard>
  )
}
